import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Clock, Mail, Save, Play, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConfigSection({ buildingId, token }) {
  const [config, setConfig] = useState({
    plan_comptable: 'simplifie',
    frequence_appels: 'trimestriel',
    jour_emission_appels: 1,
    delai_paiement_jours: 30,
    relances_auto_actives: true,
    delai_relance_niveau1: 15,
    delai_relance_niveau2: 30,
    delai_relance_niveau3: 45,
    stripe_actif: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/config/${buildingId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  }, [buildingId, token]);

  const fetchAutoStatus = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/relances/auto/status?building_id=${buildingId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAutoStatus(data);
      }
    } catch (error) {
      console.error('Error fetching auto status:', error);
    }
  }, [buildingId, token]);

  useEffect(() => {
    fetchConfig();
    fetchAutoStatus();
  }, [fetchConfig, fetchAutoStatus]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/config/${buildingId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuration enregistrée');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleExecuteNow = async () => {
    setExecuting(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/relances/auto/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.reminders_sent} relance(s) envoyée(s)`);
        fetchAutoStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'exécution');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Configuration Comptabilité</h2>
          <p className="text-sm text-gray-500">Paramètres de facturation et relances automatiques</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00B892] disabled:opacity-50"
          data-testid="save-config-btn"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          Enregistrer
        </button>
      </div>

      {/* General Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-gray-400" />
          Paramètres généraux
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan comptable</label>
            <select
              value={config.plan_comptable}
              onChange={(e) => setConfig({ ...config, plan_comptable: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              data-testid="plan-comptable-select"
            >
              <option value="simplifie">Simplifié (petites copros)</option>
              <option value="standard">Standard</option>
              <option value="complet">Complet (grandes copros)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fréquence des appels</label>
            <select
              value={config.frequence_appels}
              onChange={(e) => setConfig({ ...config, frequence_appels: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              data-testid="frequence-select"
            >
              <option value="mensuel">Mensuel</option>
              <option value="trimestriel">Trimestriel</option>
              <option value="semestriel">Semestriel</option>
              <option value="annuel">Annuel</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jour d'émission des appels</label>
            <input
              type="number"
              min="1"
              max="28"
              value={config.jour_emission_appels}
              onChange={(e) => setConfig({ ...config, jour_emission_appels: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              data-testid="jour-emission-input"
            />
            <p className="text-xs text-gray-500 mt-1">Jour du mois (1-28)</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Délai de paiement (jours)</label>
            <input
              type="number"
              min="7"
              max="90"
              value={config.delai_paiement_jours}
              onChange={(e) => setConfig({ ...config, delai_paiement_jours: parseInt(e.target.value) || 30 })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              data-testid="delai-paiement-input"
            />
          </div>
        </div>
      </div>

      {/* Automatic Reminders */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Mail className="h-5 w-5 text-gray-400" />
            Relances automatiques
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={config.relances_auto_actives}
              onChange={(e) => setConfig({ ...config, relances_auto_actives: e.target.checked })}
              className="w-5 h-5 text-[#00D3A8] rounded border-gray-300 focus:ring-[#00D3A8]"
              data-testid="relances-auto-toggle"
            />
            <span className="text-sm font-medium text-gray-700">Activées</span>
          </label>
        </div>
        
        {config.relances_auto_actives && (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Exécution quotidienne à 9h00</p>
                  <p className="text-xs text-blue-600 mt-1">
                    Les relances sont envoyées automatiquement chaque jour pour les impayés dépassant les délais configurés.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">1</div>
                  <span className="font-medium text-orange-800">Rappel amiable</span>
                </div>
                <label className="text-sm text-gray-600">Après</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={config.delai_relance_niveau1}
                    onChange={(e) => setConfig({ ...config, delai_relance_niveau1: parseInt(e.target.value) || 15 })}
                    className="w-20 px-3 py-2 border border-orange-200 rounded-lg text-center"
                    data-testid="delai-n1-input"
                  />
                  <span className="text-sm text-gray-600">jours de retard</span>
                </div>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center text-sm font-bold">2</div>
                  <span className="font-medium text-red-800">Relance ferme</span>
                </div>
                <label className="text-sm text-gray-600">Après</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    min="1"
                    max="90"
                    value={config.delai_relance_niveau2}
                    onChange={(e) => setConfig({ ...config, delai_relance_niveau2: parseInt(e.target.value) || 30 })}
                    className="w-20 px-3 py-2 border border-red-200 rounded-lg text-center"
                    data-testid="delai-n2-input"
                  />
                  <span className="text-sm text-gray-600">jours de retard</span>
                </div>
              </div>
              
              <div className="bg-red-100 rounded-lg p-4 border border-red-300">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-red-700 text-white flex items-center justify-center text-sm font-bold">3</div>
                  <span className="font-medium text-red-900">Mise en demeure</span>
                </div>
                <label className="text-sm text-gray-600">Après</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={config.delai_relance_niveau3}
                    onChange={(e) => setConfig({ ...config, delai_relance_niveau3: parseInt(e.target.value) || 45 })}
                    className="w-20 px-3 py-2 border border-red-300 rounded-lg text-center"
                    data-testid="delai-n3-input"
                  />
                  <span className="text-sm text-gray-600">jours de retard</span>
                </div>
              </div>
            </div>
            
            {/* Manual Execute Button */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-700">Exécution manuelle</p>
                <p className="text-xs text-gray-500">Lancer les relances immédiatement sans attendre 9h00</p>
              </div>
              <button
                onClick={handleExecuteNow}
                disabled={executing}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                data-testid="execute-now-btn"
              >
                {executing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
                Exécuter maintenant
              </button>
            </div>
          </>
        )}
      </div>

      {/* Auto Status */}
      {autoStatus && config.relances_auto_actives && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Statut des relances automatiques
          </h3>
          
          {autoStatus.dernieres_relances?.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-2">Dernières relances envoyées :</p>
              {autoStatus.dernieres_relances.slice(0, 5).map((relance, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-900">{relance.coproprietaire_nom}</span>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      relance.niveau === 'niveau_1' ? 'bg-orange-100 text-orange-700' :
                      relance.niveau === 'niveau_2' ? 'bg-red-100 text-red-700' :
                      'bg-red-200 text-red-800'
                    }`}>
                      {relance.niveau === 'niveau_1' ? 'Rappel' : relance.niveau === 'niveau_2' ? 'Relance' : 'Mise en demeure'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(relance.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-300" />
              <p>Aucune relance automatique envoyée récemment</p>
            </div>
          )}
          
          {autoStatus.relances_en_attente?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Prochaines relances prévues ({autoStatus.relances_en_attente.length})
              </p>
              <div className="space-y-2">
                {autoStatus.relances_en_attente.slice(0, 3).map((pending, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{pending.coproprietaire_nom}</span>
                    <span className="text-gray-500">
                      {pending.jours_avant_relance > 0 
                        ? `dans ${pending.jours_avant_relance} jour(s)`
                        : 'aujourd\'hui'
                      }
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stripe Settings */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-2xl">💳</span>
          Paiements en ligne (Stripe)
        </h3>
        
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={config.stripe_actif}
            onChange={(e) => setConfig({ ...config, stripe_actif: e.target.checked })}
            className="w-5 h-5 text-[#00D3A8] rounded border-gray-300 focus:ring-[#00D3A8]"
            data-testid="stripe-toggle"
          />
          <div>
            <span className="font-medium text-gray-700">Activer les paiements par carte bancaire</span>
            <p className="text-xs text-gray-500">Les coproprietaires pourront payer leurs appels de fonds en ligne</p>
          </div>
        </label>
      </div>
    </div>
  );
}
